import {mergeMap, switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as settings from "../constants/settings";
import * as config from "../constants/config";
import * as methods from "../constants/methods";
import * as handlers from "../constants/handlers";
import * as componentTypes from "../constants/componentTypes";
import * as resources from "../constants/resources";
import * as navigationActions from "../actions/navigationActions";
import * as settingsActions from "../actions/settingsActions";
import * as responseService from "../services/rccResponseService";
import * as asyncResponseService from "../services/rccAsyncResponseService";
import * as eventResponseService from "../services/rccEventResponseService";
import * as windowService from "../services/windowService";
import * as reducerHelper from "../helpers/reducerHelper";
import * as validationHelper from "../helpers/validationHelper";
import * as translationHelper from "../helpers/translationHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as messageHelper from "../helpers/messageHelper";
import * as getterHelper from "../helpers/getterHelper";
import * as formatHelper from "../helpers/formatHelper";
import * as printHelper from "../helpers/printHelper";

const connectionOpenedAction = (action$) =>
    action$.pipe(
        ofType(actionTypes.RCC_WEBSOCKET_OPENED),
        switchMap(() => {
            return [
                {
                    type: actionTypes.RCC_SIMPLE_REQUEST,
                    payload: {
                        type: methods.METHOD_RCC_GET_VERSION
                    },
                    method: handlers.RCC_GET_VERSION,
                    tabId: 0
                }
            ];
        })
    );

const connectionError = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RCC_WEBSOCKET_ERROR),
        switchMap(() => {
            if (state$.value.global.initialized) {
                return [];
            }

            return [
                {
                    type: actionTypes.RCC_INITIALIZED
                }
            ];
        })
    );

/**
 * Dispatchs METHOD_MACHINE_IS_CONNECTED to trigger actions for machine connection.
 */
const connectMachineAction = (action$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_CONNECT),
        switchMap(() => {
            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_IS_CONNECTED
                    },
                    method: handlers.MACHINE_IS_CONNECTED,
                    tabId: 0,
                    safe: false
                }
            ];
        })
    );

const dispenseFormulaAction = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_DISPENSE_FORMULA),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            let dispensedFormula = state$.value.formula[payload.tabId];

            if (
                dispensedFormula.product === null ||
                dispensedFormula.fandeck === null ||
                dispensedFormula.formula === null ||
                dispensedFormula.base === null ||
                dispensedFormula.package === null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_START_DISPENSE_FORMULA,
                        params: {
                            name: dispensedFormula.formula.name,
                            displayName: dispensedFormula.formula.displayName,
                            formula: dispensedFormula.formula.getColorantVolumes(),
                            numCans: dispensedFormula.formula.isCorrection() ? 1 : dispensedFormula.numCanTotal,
                            numCansDone: dispensedFormula.formula.isCorrection() ? 0 : dispensedFormula.numCanDone,
                            customCan: dispensedFormula.package.isCustom(),
                            details: [
                                dispensedFormula.product.name,
                                dispensedFormula.base.name,
                                dispensedFormula.package.name
                            ],
                            displayDetails: [
                                dispensedFormula.product.displayName,
                                dispensedFormula.base.displayName,
                                dispensedFormula.package.displayName
                            ],
                            shelf: [-1, 0, -1],
                            punch: 0,
                            info: {
                                formulaName: dispensedFormula.formula.name,
                                colorantList: reducerHelper.processColorantsForTmc(
                                    dispensedFormula.formula.colorantList
                                ),
                                package: {
                                    name: dispensedFormula.package.name,
                                    nominalAmount: dispensedFormula.package.nominalAmount,
                                    fillingAmount: dispensedFormula.package.filling,
                                    isGravimetric: dispensedFormula.package.isGravimetric
                                },
                                fandeckName: dispensedFormula.fandeck.name,
                                productName: dispensedFormula.product.name,
                                base: {
                                    name: dispensedFormula.base.name,
                                    density: dispensedFormula.base.density
                                }
                            },
                            tmcData: dispensedFormula.tmcData,
                            infoData: dispensedFormula.infoData,
                            meta: {
                                colorStatsId: payload.colorStatsId,
                                tabId: payload.tabId,
                                quickTint: false,
                                orderId: null
                            }
                        }
                    },
                    method: handlers.MACHINE_START_DISPENSE_FORMULA,
                    tabId: payload.tabId,
                    safe: true
                },
                {
                    type: actionTypes.SAVE_DISPENSING_FORMULA,
                    payload: {
                        formula: dispensedFormula.formula,
                        base: dispensedFormula.base,
                        moreInfoText: dispensedFormula.moreInfoText,
                        dispenseNote: dispensedFormula.dispenseNote,
                        moreInfoInternal: dispensedFormula.moreInfoInternal,
                        buildingSite: dispensedFormula.buildingSite,
                        priceList: modelHelper.formatPricesForStats(dispensedFormula.priceList),
                        infoData: dispensedFormula.infoData,
                        printData: dispensedFormula.printData,
                        tmcData: dispensedFormula.tmcData
                    }
                }
            ];
        })
    );

const dispenseHistoryFormulaAction = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_DISPENSE_HISTORY_FORMULA),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            let dispensedFormula = state$.value.formula[payload.tabId];

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_START_DISPENSE_FORMULA,
                        params: {
                            name: dispensedFormula.formula.name,
                            displayName: dispensedFormula.formula.displayName,
                            formula: dispensedFormula.formula.getColorantVolumes(),
                            numCans: dispensedFormula.numCanTotal,
                            numCansDone: 0,
                            customCan: dispensedFormula.package !== null ? dispensedFormula.package.isCustom() : null,
                            details: [
                                dispensedFormula.product !== null ? dispensedFormula.product.name : null,
                                dispensedFormula.base !== null ? dispensedFormula.base.name : null,
                                dispensedFormula.package !== null ? dispensedFormula.package.name : null
                            ],
                            displayDetails: [
                                dispensedFormula.product !== null ? dispensedFormula.product.displayName : null,
                                dispensedFormula.base !== null ? dispensedFormula.base.displayName : null,
                                dispensedFormula.package !== null ? dispensedFormula.package.displayName : null
                            ],
                            shelf: [-1, 0, -1],
                            punch: 0,
                            info: {
                                formulaName: dispensedFormula.formula.name,
                                colorantList: reducerHelper.processColorantsForTmc(
                                    dispensedFormula.formula.colorantList
                                ),
                                package:
                                    dispensedFormula.package !== null
                                        ? {
                                              name: dispensedFormula.package.name,
                                              nominalAmount: dispensedFormula.package.nominalAmount,
                                              fillingAmount: dispensedFormula.package.filling,
                                              isGravimetric: dispensedFormula.package.isGravimetric
                                          }
                                        : null,
                                fandeckName: dispensedFormula.fandeck !== null ? dispensedFormula.fandeck.name : null,
                                productName: dispensedFormula.product !== null ? dispensedFormula.product.name : null,
                                base:
                                    dispensedFormula.base !== null
                                        ? {
                                              name: dispensedFormula.base.name,
                                              density: dispensedFormula.base.density
                                          }
                                        : null
                            },
                            tmcData: dispensedFormula.tmcData,
                            infoData: dispensedFormula.infoData,
                            meta: {
                                colorStatsId: payload.colorStatsId,
                                tabId: payload.tabId,
                                quickTint: false,
                                orderId: null
                            }
                        }
                    },
                    method: handlers.MACHINE_START_DISPENSE_FORMULA,
                    tabId: payload.tabId,
                    safe: true
                },
                {
                    type: actionTypes.SAVE_DISPENSING_FORMULA,
                    payload: {
                        formula: dispensedFormula.formula,
                        base: dispensedFormula.base,
                        moreInfoText: dispensedFormula.moreInfoText,
                        dispenseNote: dispensedFormula.dispenseNote,
                        moreInfoInternal: dispensedFormula.moreInfoInternal,
                        buildingSite: dispensedFormula.buildingSite,
                        priceList: modelHelper.formatPricesForStats(dispensedFormula.priceList),
                        infoData: dispensedFormula.infoData,
                        printData: dispensedFormula.printData,
                        tmcData: dispensedFormula.tmcData
                    }
                }
            ];
        })
    );

const initQuickDispensing = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.INIT_QUICK_DISPENSING),
        switchMap(({payload}) => {
            let initActions = [
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.QUICK_TINT_MODAL
                    }
                }
            ];

            if (state$.value.module.redlikeLightSoftware && state$.value.settings.quickTintWindowCloseAfterDispensing) {
                initActions.push({
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        type: componentTypes.DISPENSING_QUICK_TINT_MODAL,
                        tabId: componentTypes.MODAL_NO_TAB
                    }
                });
            }

            let quickTintFormula = payload.formula.updateColorantDispenseLogicForQuickTint();

            if (payload.dispense) {
                initActions.push({
                    type: actionTypes.SAVE_DISPENSING_FORMULA,
                    payload: {
                        formula: quickTintFormula,
                        base: null,
                        moreInfoText: null,
                        dispenseNote: null,
                        moreInfoInternal: null,
                        buildingSite: null,
                        priceList: [],
                        infoData: null,
                        printData: null,
                        tmcData: null
                    }
                });

                initActions.push({
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_VALIDATE_FORMULA,
                        params: {
                            formula: quickTintFormula.getColorantVolumes(),
                            numCansDone: 0,
                            numCans: 1,
                            customCan: false
                        }
                    },
                    method: handlers.MACHINE_VALIDATE_QUICK_DISPENSE_FORMULA,
                    tabId: componentTypes.QUICK_TINT_TAB_ID,
                    safe: true
                });
            }

            if (!payload.load) {
                return initActions;
            }

            initActions.push({
                type: actionTypes.ADD_NEW_MAIN_TAB,
                payload: {
                    tabId: componentTypes.QUICK_TINT_TAB_ID,
                    color: quickTintFormula.color,
                    scene: null
                }
            });

            initActions.push({
                type: actionTypes.COLORANT_PANEL_INIT,
                payload: {
                    tabId: componentTypes.QUICK_TINT_TAB_ID,
                    type: componentTypes.MAIN_TAB
                }
            });

            initActions.push({
                type: actionTypes.NAVIGATION_SAVE_QUICK_TINT_FORMULA,
                payload: {
                    tabId: componentTypes.QUICK_TINT_TAB_ID,
                    formula: quickTintFormula,
                    margin: getterHelper.getDefaultMargin(
                        state$.value.global.allMarginList,
                        state$.value.pricing.showMargin,
                        state$.value.pricing.showMarkup,
                        state$.value.pricing.showDiscount
                    )
                }
            });

            initActions.push({
                type: actionTypes.RELOAD_FORMULA_META_DATA,
                payload: {
                    tabId: componentTypes.QUICK_TINT_TAB_ID
                }
            });

            initActions.push({
                type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                payload: {
                    tabId: componentTypes.QUICK_TINT_TAB_ID,
                    formula: quickTintFormula.getColorantVolumes(),
                    numCanDone: 0,
                    numCanTotal: 1,
                    customCan: false
                }
            });

            return initActions;
        })
    );

const startQuickDispensing = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.START_QUICK_DISPENSING),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.SAVE_DISPENSING_FORMULA,
                    payload: {
                        formula: formulaStore.formula,
                        base: formulaStore.base,
                        moreInfoText: formulaStore.moreInfoText,
                        dispenseNote: formulaStore.dispenseNote,
                        moreInfoInternal: formulaStore.moreInfoInternal,
                        buildingSite: formulaStore.buildingSite,
                        priceList: modelHelper.formatPricesForStats(formulaStore.priceList),
                        infoData: formulaStore.infoData,
                        printData: formulaStore.printData,
                        tmcData: formulaStore.tmcData
                    }
                },
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_START_DISPENSE_FORMULA,
                        params: {
                            name: formulaStore.formula.name,
                            displayName: formulaStore.formula.name,
                            formula: formulaStore.formula.getColorantVolumes(),
                            numCans: formulaStore.numCanTotal,
                            numCansDone: formulaStore.numCanDone,
                            customCan: false,
                            details: null,
                            displayDetails: null,
                            shelf: [-1, 0, -1],
                            punch: 0,
                            info: {
                                formulaName: null,
                                colorantList: reducerHelper.processColorantsForTmc(formulaStore.formula.colorantList),
                                package: null,
                                fandeckName: null,
                                productName: null,
                                base: null
                            },
                            tmcData: formulaStore.tmcData,
                            infoData: formulaStore.infoData,
                            meta: {
                                colorStatsId: null,
                                tabId: payload.tabId,
                                quickTint: true,
                                orderId: null
                            }
                        }
                    },
                    method: handlers.MACHINE_START_DISPENSE_FORMULA,
                    tabId: payload.tabId,
                    safe: true
                }
            ];
        })
    );

const startFormulaDispensingFromOrder = (action$) =>
    action$.pipe(
        ofType(actionTypes.TO_DISPENSE_START_FORMULA_DISPENSING),
        switchMap(({payload}) => {
            let orderData = payload.order;

            if (orderData === null) {
                return [];
            }

            if (
                orderData.formula === null ||
                orderData.productName === null ||
                orderData.base === null ||
                orderData.package === null ||
                orderData.compatible === false
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_START_DISPENSE_FORMULA,
                        params: {
                            name: orderData.formula.name,
                            displayName: orderData.formula.displayName,
                            formula: orderData.formula.getColorantVolumes(),
                            numCans: orderData.packageCount,
                            numCansDone: orderData.dispensedPackageCount,
                            customCan: orderData.package.isCustom(),
                            details: [orderData.product.name, orderData.base.name, orderData.package.name],
                            displayDetails: [
                                orderData.product.displayName,
                                orderData.base.displayName,
                                orderData.package.displayName
                            ],
                            shelf: [-1, 0, -1],
                            punch: 0,
                            info: {
                                formulaName: orderData.formula.name,
                                colorantList: reducerHelper.processColorantsForTmc(orderData.formula.colorantList),
                                package: {
                                    name: orderData.package.name,
                                    nominalAmount: orderData.package.nominalAmount,
                                    fillingAmount: orderData.package.filling,
                                    isGravimetric: orderData.package.isGravimetric
                                },
                                fandeckName: orderData.fandeck.name,
                                productName: orderData.product.name,
                                base: {
                                    name: orderData.base.name,
                                    density: orderData.base.density
                                }
                            },
                            tmcData: payload.tmcData,
                            infoData: payload.infoData,
                            meta: {
                                colorStatsId: payload.colorStatsId,
                                tabId: payload.tabId,
                                quickTint: false,
                                orderId: orderData.id
                            }
                        }
                    },
                    method: handlers.MACHINE_START_DISPENSE_FORMULA,
                    tabId: payload.tabId,
                    safe: true
                },
                {
                    type: actionTypes.SAVE_DISPENSING_FORMULA,
                    payload: {
                        formula: orderData.formula,
                        base: orderData.base,
                        moreInfoText: orderData.formulaMoreInfo,
                        dispenseNote: orderData.note,
                        moreInfoInternal: null,
                        buildingSite: null,
                        priceList: [],
                        infoData: payload.infoData,
                        printData: payload.printData,
                        tmcData: payload.tmcData
                    }
                }
            ];
        })
    );

const websocketResponse = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RCC_WEBSOCKET_RESPONSE),
        mergeMap(({payload}) => {
            let response = null;

            try {
                response = JSON.parse(payload.data);
            } catch (e) {
                response = null;
            }

            if (response === null) {
                windowService.logResponse("RCC   ", payload.data);
                return [{type: actionTypes.UNKNOWN_ACTION}];
            }

            if (response.sequence === undefined || isNaN(response.sequence)) {
                windowService.logResponse("RCC   ", payload.data);

                if (response.event !== undefined) {
                    return eventResponseService.processWebsocketEventResponse(response, state$.value);
                }

                return [{type: actionTypes.UNKNOWN_RCC_ACTION}];
            }

            if (state$.value.rcc.expected.some((expected) => expected.id === response.sequence)) {
                let expectedRequest = state$.value.rcc.expected.find((expected) => expected.id === response.sequence);

                if (expectedRequest === undefined) {
                    windowService.logResponse("RCC   ", payload.data);
                    return [
                        {
                            type: actionTypes.REMOVE_RCC_ASYNC_RESPONSE,
                            payload: {
                                id: response.sequence
                            }
                        }
                    ];
                }

                windowService.logResponse("RCC   ", payload.data, expectedRequest.time);

                return asyncResponseService
                    .processWebsocketAsyncResponse(
                        expectedRequest.tabId,
                        response.event,
                        response,
                        state$.value,
                        expectedRequest.next
                    )
                    .concat([
                        {
                            type: actionTypes.REMOVE_RCC_ASYNC_RESPONSE,
                            payload: {
                                id: response.sequence
                            }
                        }
                    ]);
            }

            let defaultRequests = [
                {
                    type: actionTypes.REMOVE_RCC_WEBSOCKET_REQUEST,
                    payload: {
                        sequence: response.sequence
                    }
                }
            ];

            let request = state$.value.rcc.requests.find((request) => request.id === response.sequence);

            if (request === undefined) {
                windowService.logResponse("RCC   ", payload.data);
                return defaultRequests;
            }

            windowService.logResponse("RCC   ", payload.data, request.time);

            return defaultRequests.concat(
                responseService.processWebsocketResponse(
                    request.tabId,
                    request.method,
                    response,
                    request.next,
                    state$.value
                )
            );
        })
    );

const loadErrorDetails = (action$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_MACHINE_ERRORS),
        switchMap(({payload}) => {
            if (payload.errors.length > 0) {
                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_GET_ERROR_DETAILS,
                            params: {
                                dispenserType: payload.errors[payload.errors.length - 1].type,
                                code: payload.errors[payload.errors.length - 1].code
                            }
                        },
                        method: handlers.MACHINE_GET_ERROR_DETAILS,
                        tabId: 0,
                        safe: false
                    }
                ];
            } else {
                return [];
            }
        })
    );

const printOrderWhileDispensing = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.TO_DISPENSE_PRINT_ORDER_WHILE_DISPENSING),
        switchMap(({payload}) => {
            let orderData = state$.value.toDispense.dispensedOrder;

            if (orderData === null || orderData.id !== payload.orderId || orderData.formula === null) {
                return [];
            }

            let template = validationHelper.getTemplate(
                state$.value.rcc.templateList,
                payload.printTask.template,
                state$.value.settings.printerTemplate
            );

            if (template === null) {
                return [messageHelper.getWarningMessageAction("flashMessage.noTemplate")];
            }

            let printParams = {
                printerName: validationHelper.getPrinterSafe(
                    state$.value.rcc.printerList,
                    payload.printTask.printer,
                    state$.value.settings.printer
                ),
                template: template,
                pages: payload.printTask.copies,
                printAsImage: state$.value.settings.printAsImage,
                values: {
                    client_name: state$.value.module.clientIdentifier,
                    tintometric_system:
                        state$.value.global.tintometricSystem === null
                            ? null
                            : state$.value.global.tintometricSystem.name,
                    scaling: orderData.formula.scaling * 100,
                    formula_note: orderData.formulaMoreInfo,
                    dispensing_note: orderData.note,
                    dispensing_more_info_internal: null,
                    user: state$.value.login.loggedUser.login,
                    building_site: null,
                    ...orderData.formula.getPrintData(state$.value.settings.formulaUnits),
                    ...printHelper.formatCurrentPrintData(state$.value.rcc.dispensingFormula.printData),
                    ...translationHelper.getPrinterTemplateTranslation(),
                    packageordercount: payload.numCanDone + "/" + orderData.packageCount
                }
            };

            if (payload.printTask.preview) {
                return [
                    {
                        type: actionTypes.NAVIGATION_OPEN_MODAL,
                        payload: {
                            tabId: componentTypes.MODAL_NO_TAB,
                            type: componentTypes.PRINT_TASK_PREVIEW_MODAL,
                            params: {
                                printParams: printParams
                            }
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.START_PRINT_REPORT_SILENT,
                    payload: {
                        tabId: null,
                        template: printParams.template,
                        printer: printParams.printerName,
                        pages: printParams.pages,
                        printValues: printParams.values,
                        formula: orderData.formula,
                        printData: null,
                        moreInfoText: null,
                        dispenseNote: null,
                        moreInfoInternal: null
                    }
                }
            ];
        })
    );

const printFormulaWhileDispensing = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.PRINT_FORMULA_WHILE_DISPENSING),
        switchMap(({payload}) => {
            let dispensingFormula = state$.value.rcc.dispensingFormula;

            if (dispensingFormula === null) {
                return [];
            }

            let template = validationHelper.getTemplate(
                state$.value.rcc.templateList,
                payload.printTask.template,
                state$.value.settings.printerTemplate
            );

            if (template === null) {
                return [messageHelper.getWarningMessageAction("flashMessage.noTemplate")];
            }

            let printData = dispensingFormula.printData ?? {packagecount: "1"};
            let printParams = {
                printerName: validationHelper.getPrinterSafe(
                    state$.value.rcc.printerList,
                    payload.printTask.printer,
                    state$.value.settings.printer
                ),
                template: template,
                pages: payload.printTask.copies,
                printAsImage: state$.value.settings.printAsImage,
                values: {
                    client_name: state$.value.module.clientIdentifier,
                    tintometric_system:
                        state$.value.global.tintometricSystem === null
                            ? null
                            : state$.value.global.tintometricSystem.name,
                    scaling: dispensingFormula.formula.scaling * 100,
                    formula_note: dispensingFormula.moreInfoText,
                    dispensing_note: dispensingFormula.dispenseNote,
                    dispensing_more_info_internal: dispensingFormula.moreInfoInternal,
                    user: state$.value.login.loggedUser.login,
                    building_site: dispensingFormula.buildingSite,
                    ...dispensingFormula.formula.getPrintData(state$.value.settings.formulaUnits),
                    ...printHelper.formatCurrentPrintData(printData),
                    ...translationHelper.getPrinterTemplateTranslation(),
                    packageordercount: payload.numCanDone + "/" + printData.packagecount
                }
            };

            if (payload.printTask.preview) {
                return [
                    {
                        type: actionTypes.NAVIGATION_OPEN_MODAL,
                        payload: {
                            tabId: componentTypes.MODAL_NO_TAB,
                            type: componentTypes.PRINT_TASK_PREVIEW_MODAL,
                            params: {
                                printParams: printParams
                            }
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.START_PRINT_REPORT_SILENT,
                    payload: {
                        tabId: null,
                        template: printParams.template,
                        printer: printParams.printerName,
                        pages: printParams.pages,
                        printValues: printParams.values,
                        formula: dispensingFormula.formula,
                        printData: null,
                        moreInfoText: null,
                        dispenseNote: null,
                        moreInfoInternal: null
                    }
                }
            ];
        })
    );

const dialogContinue = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_DIALOG_CONTINUE),
        switchMap(() => {
            let dialog = state$.value.rcc.machine.dialog;
            if (dialog === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_DIALOG_BUTTON_CLICK,
                        params: {
                            dialogID: dialog.id,
                            msgBtn: "continue"
                        }
                    },
                    method: handlers.MACHINE_DIALOG_BUTTON_CLICK,
                    tabId: 0,
                    safe: false
                }
            ];
        })
    );

const updateRefillValidationWizard = (action$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_UPDATE_REFILL_VALIDATION_WIZARD),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_GET_CANISTER_IMAGE,
                        params: {
                            index: payload.index
                        }
                    },
                    method: handlers.MACHINE_GET_CANISTER_IMAGE,
                    tabId: 0,
                    safe: true
                }
            ];
        })
    );

const confirmRefillValidationWizard = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_CONFIRM_REFILL_VALIDATION),
        switchMap(({payload}) => {
            if (
                state$.value.module.gammaSoftware ||
                (state$.value.rcc.machine.refillValidation !== null &&
                    (state$.value.settings.ufiCodeLogic === settings.UFI_CODE_LOGIC_DISABLED ||
                        state$.value.settings.ufiCodeLogic === settings.UFI_CODE_LOGIC_DB_VALUES))
            ) {
                return [
                    {
                        type: actionTypes.MACHINE_REFILL_CANISTER,
                        payload: {
                            index: payload.index,
                            level: payload.level,
                            add: payload.add,
                            unitMl: payload.unitMl,
                            type: payload.type,
                            ufiCode: null
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: 0,
                        type: componentTypes.REFILL_CONFIRMATION_MODAL,
                        params: {
                            index: payload.index,
                            level: payload.level,
                            add: payload.add,
                            unitMl: payload.unitMl
                        }
                    }
                }
            ];
        })
    );

const refillCanister = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_REFILL_CANISTER),
        switchMap(({payload}) => {
            // Define actions that will be executed, close modal is always necessary
            let actions = [
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.REFILL_CONFIRMATION_MODAL
                    }
                }
            ];

            // Define variables
            let methodType, method, levelIncreased;

            // Define params object that will be send
            const params = {
                index: payload.index,
                actionType: payload.type
            };

            // Get canister object for canister that is being manipulated
            const canister = state$.value.rcc.canisterList.find((canister) => canister.id === payload.index);

            // Set new level parameter according to selected units
            if (payload.unitMl) {
                params.levelMl = payload.level;
                levelIncreased = payload.add || canister.currentLevelMl < payload.level;
            } else {
                params.level = payload.level;
                levelIncreased = payload.add || canister.currentLevel < payload.level;
            }

            // Ufi code should not be sent if level decreased or is not manually editable
            if (
                levelIncreased &&
                state$.value.settings.ufiCodeLogic !== settings.UFI_CODE_LOGIC_DISABLED &&
                state$.value.settings.ufiCodeLogic !== settings.UFI_CODE_LOGIC_DB_VALUES
            ) {
                params.ufiCode = payload.ufiCode;
            }

            // Batch number should not be sent if level decreased or is not manually editable
            if (
                levelIncreased &&
                state$.value.settings.colorantBatchNumberLogic !== settings.COLORANT_BATCH_NUMBER_LOGIC_DISABLED
            ) {
                params.batchNumber = payload.batchNumber;
            }

            // Set method and handler according to action performed
            if (payload.add) {
                methodType = methods.METHOD_MACHINE_ADD_CANISTER_LEVEL;
                method = handlers.MACHINE_ADD_CANISTER_LEVEL;
            } else {
                methodType = methods.METHOD_MACHINE_SET_CANISTER_LEVEL;
                method = handlers.MACHINE_SET_CANISTER_LEVEL;

                // Stock removal confirmation should display only if level increased and is gammaSoftware
                if (state$.value.login.loggedUser.canRead(resources.COMPONENT_COLORANT_STOCK_MODAL) && levelIncreased) {
                    actions.push(
                        navigationActions.openModalWithParams(
                            componentTypes.MODAL_NO_TAB,
                            componentTypes.REMOVE_PACKAGE_FROM_STOCK_CONFIRM_MODAL,
                            {
                                colorant: canister.colorant.name
                            }
                        )
                    );
                }
            }

            actions.push({
                type: actionTypes.RCC_REQUEST,
                payload: {
                    type: methodType,
                    params: params
                },
                method: method,
                tabId: 0,
                safe: true
            });

            return actions;
        })
    );

const stockCalculatePrediction = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.STOCK_CALCULATE_PREDICTION),
        switchMap(() => {
            if (state$.value.login.loggedUser.canNotRead(resources.COMPONENT_COLORANT_STOCK_MODAL)) {
                return [];
            }

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_COLORANT_PREDICTION
                    },
                    method: handlers.MACHINE_COLORANT_PREDICTION,
                    tabId: 0,
                    safe: true
                }
            ];
        })
    );

const setManualColorantAdditionUnit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SET_MANUAL_COLORANT_ADDITION_UNIT),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SAVE_MACHINE_DIALOG_TEXT,
                    payload: {
                        text: modelHelper.getCustomUnitDialogText(
                            state$.value.rcc.machine.dialog,
                            payload.unit,
                            state$.value.global.allColorantList,
                            state$.value.settings.shotsName,
                            state$.value.settings.shotsCoef
                        )
                    }
                },
                settingsActions.setManualAdditionUnit(payload.unit)
            ];
        })
    );

const setRccUrl = (action$) =>
    action$.pipe(
        ofType(actionTypes.RCC_SET_URL),
        switchMap(({payload}) => {
            if (!validationHelper.isHostValid(payload.url)) {
                return [messageHelper.getWarningMessageAction("flashMessage.invalidUrl")];
            }

            windowService.setDefaultRcc(payload.url);

            return [
                {
                    type: actionTypes.RCC_SAVE_URL,
                    payload: {
                        url: payload.url
                    }
                }
            ];
        })
    );

const validateDispensedFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA),
        switchMap(({payload}) => {
            if (!state$.value.rcc.machine.validMethod(methods.METHOD_MACHINE_MANUAL_FORMULA)) {
                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_VALIDATE_FORMULA,
                            params: {
                                formula: payload.formula,
                                numCansDone: payload.numCanDone,
                                numCans: payload.numCanTotal,
                                customCan: payload.customCan
                            }
                        },
                        method: handlers.MACHINE_VALIDATE_FORMULA,
                        tabId: payload.tabId,
                        safe: true
                    }
                ];
            }

            if (state$.value.rcc.machine.validMethod(methods.METHOD_MACHINE_VALIDATE_FORMULA)) {
                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_VALIDATE_FORMULA,
                            params: {
                                formula: payload.formula,
                                numCansDone: payload.numCanDone,
                                numCans: payload.numCanTotal,
                                customCan: payload.customCan
                            }
                        },
                        method: handlers.MACHINE_VALIDATE_FORMULA_AND_LOAD_MANUAL,
                        tabId: payload.tabId,
                        safe: true
                    }
                ];
            }

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_MANUAL_FORMULA,
                        params: {
                            formula: payload.formula
                        }
                    },
                    method: handlers.MACHINE_MANUAL_FORMULA,
                    tabId: payload.tabId,
                    safe: true
                }
            ];
        })
    );

const getPackagesForColorant = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_ACTIVE_CANISTER),
        switchMap(() => {
            if (state$.value.rcc.activeCanister.colorant === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PACKAGES_FOR_COLORANT,
                        params: {
                            colorantName: state$.value.rcc.activeCanister.colorant.name
                        }
                    },
                    method: handlers.GET_PACKAGES_FOR_COLORANT,
                    tabId: 0
                }
            ];
        })
    );

const getMachineCanisters = (action$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_MACHINE_CONFIGURATION_AND_RELOAD_CANISTERS),
        switchMap(() => {
            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_CANISTERS
                    },
                    method: handlers.MACHINE_CANISTERS,
                    tabId: 0,
                    safe: false
                }
            ];
        })
    );

const validateAndSetCanistersBarcodes = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MACHINE_VALIDATE_CANISTER_BARCODE),
        switchMap(({payload}) => {
            if (payload.barcode === "") {
                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_SET_CANISTER_BARCODE,
                            params: {
                                index: payload.index,
                                barcode: payload.barcode
                            }
                        },
                        method: handlers.MACHINE_SET_CANISTER_BARCODE,
                        tabId: 0,
                        safe: true
                    }
                ];
            }

            let unique = true;
            for (let canister of state$.value.rcc.canisterList) {
                if (canister.barcode === payload.barcode) {
                    unique = false;
                }
            }

            if (unique) {
                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_SET_CANISTER_BARCODE,
                            params: {
                                index: payload.index,
                                barcode: payload.barcode
                            }
                        },
                        method: handlers.MACHINE_SET_CANISTER_BARCODE,
                        tabId: 0,
                        safe: true
                    }
                ];
            }

            return [messageHelper.getWarningMessageAction("flashMessage.barcodeIsUsed")];
        })
    );

const myOrdersLoadManualFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MY_ORDERS_SAVE_DETAIL),
        switchMap(({payload}) => {
            let actions = [];
            let activeOrder = state$.value.myOrders.activeOrder;
            if (
                activeOrder !== null &&
                activeOrder.formula !== null &&
                state$.value.rcc.machine.validMethod(methods.METHOD_MACHINE_MANUAL_FORMULA)
            ) {
                actions.push({
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_MANUAL_FORMULA,
                        params: {
                            formula: activeOrder.formula.getColorantVolumes()
                        }
                    },
                    method: handlers.MACHINE_MANUAL_FORMULA_MY_ORDERS,
                    tabId: 0,
                    safe: true
                });
            }

            if (state$.value.module.redlikeLightSoftware && activeOrder !== null) {
                let baseName = activeOrder.base ? activeOrder.base.name : null;
                let productName = activeOrder.product ? activeOrder.product.name : null;
                let packageName = activeOrder.package ? activeOrder.package.name : null;

                if (baseName && productName && packageName) {
                    actions.push({
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_PRODUCT_IMAGE,
                            params: {
                                productName: productName,
                                baseName: baseName,
                                packageName: packageName,
                                online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                            }
                        },
                        method: handlers.GET_LIGHT_MY_ORDERS_PRODUCT_IMAGE,
                        tabId: payload.tabId
                    });
                }
            }

            return actions;
        })
    );

const prepareChannelAllStatsDataFile = (action$) =>
    action$.pipe(
        ofType(actionTypes.PREPARE_FILE_CHANNEL_ALL_STATS_DATA),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_PREPARE_FILE,
                        params: {
                            method: methods.MACHINE_EXPORT_CHANNEL_ALL_STATS,
                            params: {
                                startDate:
                                    payload.dateFrom !== null
                                        ? formatHelper.formatDateTimeForServer(payload.dateFrom)
                                        : null,
                                endDate:
                                    payload.dateTo !== null
                                        ? formatHelper.formatDateTimeForServer(payload.dateTo)
                                        : null
                            }
                        }
                    },
                    method: handlers.PREPARE_FILE_CHANNEL_ALL_STATS_DATA,
                    tabId: 0
                },
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: componentTypes.MODAL_NO_TAB,
                        type: componentTypes.PROGRESS_BAR_MODAL,
                        params: {
                            source: "rcc"
                        }
                    }
                }
            ];
        })
    );

const prepareChannelDispenseStatsDataFile = (action$) =>
    action$.pipe(
        ofType(actionTypes.PREPARE_FILE_CHANNEL_DISPENSE_STATS_DATA),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_PREPARE_FILE,
                        params: {
                            method: methods.MACHINE_EXPORT_CHANNEL_DISPENSE_STATS,
                            params: {
                                startDate:
                                    payload.dateFrom !== null
                                        ? formatHelper.formatDateTimeForServer(payload.dateFrom)
                                        : null,
                                endDate:
                                    payload.dateTo !== null
                                        ? formatHelper.formatDateTimeForServer(payload.dateTo)
                                        : null
                            }
                        }
                    },
                    method: handlers.PREPARE_FILE_CHANNEL_DISPENSE_STATS_DATA,
                    tabId: 0
                },
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: componentTypes.MODAL_NO_TAB,
                        type: componentTypes.PROGRESS_BAR_MODAL,
                        params: {
                            source: "rcc"
                        }
                    }
                }
            ];
        })
    );

const prepareChannelPurgeStatsDataFile = (action$) =>
    action$.pipe(
        ofType(actionTypes.PREPARE_FILE_CHANNEL_PURGE_STATS_DATA),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_PREPARE_FILE,
                        params: {
                            method: methods.MACHINE_EXPORT_CHANNEL_PURGE_STATS,
                            params: {
                                startDate:
                                    payload.dateFrom !== null
                                        ? formatHelper.formatDateTimeForServer(payload.dateFrom)
                                        : null,
                                endDate:
                                    payload.dateTo !== null
                                        ? formatHelper.formatDateTimeForServer(payload.dateTo)
                                        : null
                            }
                        }
                    },
                    method: handlers.PREPARE_FILE_CHANNEL_PURGE_STATS_DATA,
                    tabId: 0
                },
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: componentTypes.MODAL_NO_TAB,
                        type: componentTypes.PROGRESS_BAR_MODAL,
                        params: {
                            source: "rcc"
                        }
                    }
                }
            ];
        })
    );

const prepareFileSuccess = (action$) =>
    action$.pipe(
        ofType(actionTypes.RCC_PREPARE_FILE_SUCCESS),
        switchMap(({payload}) => {
            let remainingLength = payload.preparedFile.totalLength - payload.preparedFile.downloadedLength;

            if (remainingLength > config.SERVER_CHUNK_SIZE) {
                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_PREPARE_FILE_BYTES,
                            params: {
                                id: payload.preparedFile.id,
                                bytesFrom: payload.preparedFile.downloadedLength,
                                bytesTo: payload.preparedFile.downloadedLength + config.SERVER_CHUNK_SIZE
                            }
                        },
                        method: handlers.PREPARE_FILE_BYTES,
                        tabId: 0
                    },
                    {
                        type: actionTypes.RCC_UPDATE_FILE_INFORMATION,
                        payload: {
                            downloadingFile: payload.preparedFile.updateDownloadedLength(
                                payload.preparedFile.downloadedLength + config.SERVER_CHUNK_SIZE
                            )
                        }
                    }
                ];
            }

            if (remainingLength < config.SERVER_CHUNK_SIZE && remainingLength > 0) {
                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_PREPARE_FILE_BYTES,
                            params: {
                                id: payload.preparedFile.id,
                                bytesFrom: payload.preparedFile.downloadedLength,
                                bytesTo: payload.preparedFile.totalLength
                            }
                        },
                        method: handlers.PREPARE_FILE_BYTES,
                        tabId: 0
                    },
                    {
                        type: actionTypes.RCC_UPDATE_FILE_INFORMATION,
                        payload: {
                            downloadingFile: payload.preparedFile.updateDownloadedLength(
                                payload.preparedFile.downloadedLength + remainingLength
                            )
                        }
                    }
                ];
            }

            windowService.openMoveFileDialog(payload.preparedFile.filePath, payload.preparedFile.fileName);

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_RELEASE_PREPARED_FILE,
                        params: {
                            id: payload.preparedFile.id
                        }
                    },
                    method: handlers.RELEASE_PREPARED_FILE,
                    tabId: 0
                },
                {
                    type: actionTypes.RCC_DELETE_DOWNLOADED_FILE,
                    payload: {
                        downloadedFileId: payload.preparedFile.id
                    }
                },
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        tabId: componentTypes.MODAL_NO_TAB,
                        type: componentTypes.PROGRESS_BAR_MODAL
                    }
                },
                messageHelper.getSuccessMessageAction("flashMessage.fileExportComplete")
            ];
        })
    );

const prepareFileError = (action$) =>
    action$.pipe(
        ofType(actionTypes.RCC_PREPARE_FILE_ERROR),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: false
                    }
                },
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_RELEASE_PREPARED_FILE,
                        params: {
                            id: payload.preparedFileId
                        }
                    },
                    method: handlers.RELEASE_PREPARED_FILE,
                    tabId: 0
                },
                messageHelper.getErrorMessageAction("flashMessage.problemWithFileExport")
            ];
        })
    );

const syncMachineConfiguration = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SYNC_MACHINE_CONFIGURATION),
        switchMap(() => {
            if (!state$.value.rcc.connected || !state$.value.settings.overwriteMachineConfig) {
                return [];
            }

            let configuration = getterHelper.synchronizeMachineConfiguration(
                state$.value.rcc.machineConfigurationList,
                state$.value.settings.ufiCodeLogic,
                state$.value.settings.ufiCodeBaseValidation,
                state$.value.settings.rccBarcodeRefill,
                state$.value.settings.rccBaseCanBarcodeValidation,
                state$.value.settings.rccCheckCanBeforeDispense,
                state$.value.settings.rccCheckOverfill,
                state$.value.settings.colorantBatchNumberLogic,
                state$.value.settings.rccDispenseInvalidColorants,
                state$.value.settings.rccManualColorantAddition,
                state$.value.settings.rccMinimalDispenseAmount,
                state$.value.settings.rccPlaceCanSensor,
                state$.value.settings.rccPlaceCanSensorDelay,
                state$.value.settings.rccRemoveCanSensor,
                state$.value.settings.rccRemoveCanSensorDelay,
                state$.value.settings.rccSkipPlaceCanDialog,
                state$.value.settings.rccSkipRemoveCanDialog,
                state$.value.settings.rccSplitFormula,
                state$.value.settings.rccTmcEnabled,
                state$.value.settings.rccUnit,
                state$.value.settings.rccValidateColorantsDuringInit
            );

            if (configuration === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.RCC_REQUEST,
                    payload: {
                        type: methods.METHOD_MACHINE_SET_CONFIGURATION,
                        params: {
                            value: configuration
                        }
                    },
                    method: handlers.MACHINE_SET_CONFIGURATION,
                    tabId: 0,
                    safe: false
                }
            ];
        })
    );

const getPrintPreviewImage = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.GET_PRINT_PREVIEW_IMAGE),
        switchMap(({payload}) => {
            let template = payload.template;

            if (template === null) {
                template = state$.value.settings.printerTemplate;
            }

            template = validationHelper.getValidItem(state$.value.rcc.templateList, template);

            if (template === null) {
                return [messageHelper.getWarningMessageAction("flashMessage.noTemplate")];
            }

            let formula = payload.formula;

            if (formula === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                formula = state$.value.formula[payload.tabId].formula;
            }

            let printData = payload.printData;

            if (printData === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                printData = state$.value.formula[payload.tabId].printData;
            }

            let moreInfoText = payload.moreInfoText;

            if (moreInfoText === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                moreInfoText = state$.value.formula[payload.tabId].moreInfoText;
            }

            let dispenseNote = payload.dispenseNote;

            if (dispenseNote === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                dispenseNote = state$.value.formula[payload.tabId].dispenseNote;
            }

            let moreInfoInternal = payload.moreInfoInternal;

            if (
                moreInfoInternal === null &&
                payload.tabId !== null &&
                state$.value.formula[payload.tabId] !== undefined
            ) {
                moreInfoInternal = state$.value.formula[payload.tabId].moreInfoInternal;
            }

            let printValues = payload.printValues;

            if (printValues === null) {
                if (formula === null || printData === null) {
                    return [];
                }

                let activeUnit = state$.value.settings.formulaUnits;
                let buildingSite = null;
                let packageOrderCount = "1/1";

                if (state$.value.formula[payload.tabId] !== undefined) {
                    activeUnit = state$.value.formula[payload.tabId].activeUnit;
                    buildingSite = state$.value.formula[payload.tabId].buildingSite;
                    packageOrderCount = `1/${state$.value.formula[payload.tabId].numCanTotal}`;
                }

                printValues = {
                    client_name: state$.value.module.clientIdentifier,
                    tintometric_system:
                        state$.value.global.tintometricSystem === null
                            ? null
                            : state$.value.global.tintometricSystem.name,
                    scaling: formula.scaling * 100,
                    formula_note: moreInfoText,
                    dispensing_note: dispenseNote,
                    dispensing_more_info_internal: moreInfoInternal,
                    user: state$.value.login.loggedUser.login,
                    building_site: buildingSite,
                    ...formula.getPrintData(state$.value.settings.formulaUnits, activeUnit),
                    ...printHelper.formatCurrentPrintData(printData),
                    ...translationHelper.getPrinterTemplateTranslation(),
                    packageordercount: packageOrderCount
                };
            }

            if (
                state$.value.settings.formulaAutomaticNaming &&
                formula !== null &&
                (formula.scaling !== 1 || formula.modified)
            ) {
                let automaticColorName = formula.getAutomaticName(state$.value.settings.formulaUnits);

                printValues["color"] = automaticColorName;
                printValues["color_name"] = automaticColorName;
                printValues["colorname"] = automaticColorName;
                printValues["color_display_name"] = automaticColorName;
                printValues["color_print_name"] = automaticColorName;
            }

            return [
                {
                    type: actionTypes.RCC_SIMPLE_REQUEST,
                    payload: {
                        type: methods.METHOD_PRINTER_START_JPG_EXPORT,
                        params: {
                            template: template,
                            resolution: 300,
                            quality: 95,
                            values: printValues
                        }
                    },
                    method: handlers.PRINTER_START_JPG_EXPORT,
                    tabId: payload.tabId === null ? componentTypes.MODAL_NO_TAB : payload.tabId,
                    next: template
                }
            ];
        })
    );

const exportPrintPreviewToPdf = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.EXPORT_PRINT_PREVIEW_TO_PDF),
        switchMap(({payload}) => {
            let template = payload.template;

            if (template === null) {
                template = state$.value.settings.printerTemplate;
            }

            template = validationHelper.getValidItem(state$.value.rcc.templateList, template);

            if (template === null) {
                return [messageHelper.getWarningMessageAction("flashMessage.noTemplate")];
            }

            let formula = payload.formula;

            if (formula === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                formula = state$.value.formula[payload.tabId].formula;
            }

            let printData = payload.printData;

            if (printData === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                printData = state$.value.formula[payload.tabId].printData;
            }

            let moreInfoText = payload.moreInfoText;

            if (moreInfoText === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                moreInfoText = state$.value.formula[payload.tabId].moreInfoText;
            }

            let dispenseNote = payload.dispenseNote;

            if (dispenseNote === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                dispenseNote = state$.value.formula[payload.tabId].dispenseNote;
            }

            let moreInfoInternal = payload.moreInfoInternal;

            if (
                moreInfoInternal === null &&
                payload.tabId !== null &&
                state$.value.formula[payload.tabId] !== undefined
            ) {
                moreInfoInternal = state$.value.formula[payload.tabId].moreInfoInternal;
            }

            let printValues = payload.printValues;

            if (printValues === null) {
                if (formula === null || printData === null) {
                    return [];
                }

                let activeUnit = state$.value.settings.formulaUnits;
                let buildingSite = null;
                let packageOrderCount = "1/1";

                if (state$.value.formula[payload.tabId] !== undefined) {
                    activeUnit = state$.value.formula[payload.tabId].activeUnit;
                    buildingSite = state$.value.formula[payload.tabId].buildingSite;
                    packageOrderCount = `1/${state$.value.formula[payload.tabId].numCanTotal}`;
                }

                printValues = {
                    client_name: state$.value.module.clientIdentifier,
                    tintometric_system:
                        state$.value.global.tintometricSystem === null
                            ? null
                            : state$.value.global.tintometricSystem.name,
                    scaling: formula.scaling * 100,
                    formula_note: moreInfoText,
                    dispensing_note: dispenseNote,
                    dispensing_more_info_internal: moreInfoInternal,
                    user: state$.value.login.loggedUser.login,
                    building_site: buildingSite,
                    ...formula.getPrintData(state$.value.settings.formulaUnits, activeUnit),
                    ...printHelper.formatCurrentPrintData(printData),
                    ...translationHelper.getPrinterTemplateTranslation(),
                    packageordercount: packageOrderCount
                };
            }

            if (
                state$.value.settings.formulaAutomaticNaming &&
                formula !== null &&
                (formula.scaling !== 1 || formula.modified)
            ) {
                let automaticColorName = formula.getAutomaticName(state$.value.settings.formulaUnits);

                printValues["color"] = automaticColorName;
                printValues["color_name"] = automaticColorName;
                printValues["colorname"] = automaticColorName;
                printValues["color_display_name"] = automaticColorName;
                printValues["color_print_name"] = automaticColorName;
            }

            return [
                {
                    type: actionTypes.RCC_SIMPLE_REQUEST,
                    payload: {
                        type: methods.METHOD_PRINTER_START_PDF_EXPORT,
                        params: {
                            template: template,
                            resolution: 300,
                            quality: 95,
                            values: printValues
                        }
                    },
                    method: handlers.PRINTER_START_PDF_EXPORT,
                    tabId: payload.tabId === null ? componentTypes.MODAL_NO_TAB : payload.tabId
                }
            ];
        })
    );

const startPrintReportSilent = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.START_PRINT_REPORT_SILENT),
        switchMap(({payload}) => {
            let template = payload.template;

            if (template === null) {
                template = state$.value.settings.printerTemplate;
            }

            template = validationHelper.getValidItem(state$.value.rcc.templateList, template);

            if (template === null) {
                return [messageHelper.getWarningMessageAction("flashMessage.noTemplate")];
            }

            let printer = payload.printer;

            if (printer === null) {
                printer = state$.value.settings.printer;
            }

            printer = validationHelper.getPrinter(state$.value.rcc.printerList, printer);

            let formula = payload.formula;

            if (formula === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                formula = state$.value.formula[payload.tabId].formula;
            }

            let printData = payload.printData;

            if (printData === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                printData = state$.value.formula[payload.tabId].printData;
            }

            let moreInfoText = payload.moreInfoText;

            if (moreInfoText === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                moreInfoText = state$.value.formula[payload.tabId].moreInfoText;
            }

            let dispenseNote = payload.dispenseNote;

            if (dispenseNote === null && payload.tabId !== null && state$.value.formula[payload.tabId] !== undefined) {
                dispenseNote = state$.value.formula[payload.tabId].dispenseNote;
            }

            let moreInfoInternal = payload.moreInfoInternal;

            if (
                moreInfoInternal === null &&
                payload.tabId !== null &&
                state$.value.formula[payload.tabId] !== undefined
            ) {
                moreInfoInternal = state$.value.formula[payload.tabId].moreInfoInternal;
            }

            let printValues = payload.printValues;

            if (printValues === null) {
                if (formula === null || printData === null) {
                    return [];
                }

                let activeUnit = state$.value.settings.formulaUnits;
                let buildingSite = null;
                let packageOrderCount = "1/1";

                if (state$.value.formula[payload.tabId] !== undefined) {
                    activeUnit = state$.value.formula[payload.tabId].activeUnit;
                    buildingSite = state$.value.formula[payload.tabId].buildingSite;
                    packageOrderCount = `1/${state$.value.formula[payload.tabId].numCanTotal}`;
                }

                printValues = {
                    client_name: state$.value.module.clientIdentifier,
                    tintometric_system:
                        state$.value.global.tintometricSystem === null
                            ? null
                            : state$.value.global.tintometricSystem.name,
                    scaling: formula.scaling * 100,
                    formula_note: moreInfoText,
                    dispensing_note: dispenseNote,
                    dispensing_more_info_internal: moreInfoInternal,
                    user: state$.value.login.loggedUser.login,
                    building_site: buildingSite,
                    ...formula.getPrintData(state$.value.settings.formulaUnits, activeUnit),
                    ...printHelper.formatCurrentPrintData(printData),
                    ...translationHelper.getPrinterTemplateTranslation(),
                    packageordercount: packageOrderCount
                };
            }

            if (
                state$.value.settings.formulaAutomaticNaming &&
                formula !== null &&
                (formula.scaling !== 1 || formula.modified)
            ) {
                let automaticColorName = formula.getAutomaticName(state$.value.settings.formulaUnits);

                printValues["color"] = automaticColorName;
                printValues["color_name"] = automaticColorName;
                printValues["colorname"] = automaticColorName;
                printValues["color_display_name"] = automaticColorName;
                printValues["color_print_name"] = automaticColorName;
            }

            return [
                {
                    type: actionTypes.RCC_SIMPLE_REQUEST,
                    payload: {
                        type: methods.METHOD_RCC_PRINT_FORMULA_SILENT,
                        params: {
                            printerName: printer,
                            template: template,
                            pages: payload.pages === null ? 1 : payload.pages,
                            printAsImage: state$.value.settings.printAsImage,
                            values: printValues
                        }
                    },
                    method: handlers.RCC_PRINT_FORMULA_SILENT,
                    tabId: payload.tabId === null ? componentTypes.MODAL_NO_TAB : payload.tabId,
                    next: template
                }
            ];
        })
    );

export default [
    connectionOpenedAction,
    connectionError,
    connectMachineAction,
    dispenseFormulaAction,
    dispenseHistoryFormulaAction,
    initQuickDispensing,
    startQuickDispensing,
    startFormulaDispensingFromOrder,
    websocketResponse,
    loadErrorDetails,
    printOrderWhileDispensing,
    printFormulaWhileDispensing,
    dialogContinue,
    updateRefillValidationWizard,
    confirmRefillValidationWizard,
    refillCanister,
    stockCalculatePrediction,
    setManualColorantAdditionUnit,
    setRccUrl,
    validateDispensedFormula,
    getPackagesForColorant,
    getMachineCanisters,
    validateAndSetCanistersBarcodes,
    myOrdersLoadManualFormula,
    prepareChannelAllStatsDataFile,
    prepareChannelDispenseStatsDataFile,
    prepareChannelPurgeStatsDataFile,
    prepareFileSuccess,
    prepareFileError,
    syncMachineConfiguration,
    getPrintPreviewImage,
    exportPrintPreviewToPdf,
    startPrintReportSilent
];
